/*
 * Strap progress bar – PhenomeStraps
 * Counts only products whose product type is "Nylon Watch Strap",
 * so spring bars and tools never move the bar.
 * Tiers: 2 straps = free shipping (The Pair), 3+ = 10% (The Rotation),
 *   5+ = 15% (The Collector's Set).
 */
(() => {
  if (customElements.get('strap-progress')) return;

  const STRAP_TYPE = 'nylon watch strap';
  const MARKS = [
    { n: 2, label: 'Free shipping' },
    { n: 3, label: '10% off' },
    { n: 5, label: '15% off' },
  ];

  const ICON_TRUCK =
    '<svg class="strap-progress__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h11v9H3z"/><path d="M14 9h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>';
  const ICON_GIFT =
    '<svg class="strap-progress__icon" viewBox="0 0 24 24" fill="none" stroke="#1f7a4d" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.7 2.7L16 9.8"/></svg>';

  let pending = null;
  const fetchCart = () => {
    if (!pending) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      pending = fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .finally(() => setTimeout(() => (pending = null), 0));
    }
    return pending;
  };

  const isStrap = (type) => String(type || '').trim().toLowerCase() === STRAP_TYPE;
  const countStraps = (cart) => cart.items.reduce((sum, i) => (isStrap(i.product_type) ? sum + i.quantity : sum), 0);
  const plural = (n) => (n === 1 ? 'strap' : 'straps');

  const message = (n) => {
    if (n <= 0) return 'Free shipping from 2 straps <span aria-hidden="true">&middot;</span> 10% off 3+ <span aria-hidden="true">&middot;</span> 15% off 5+';
    if (n === 1) return '<strong>Add 1 more strap</strong> for free worldwide shipping';
    if (n === 2) return '<strong>Free shipping unlocked.</strong> Add 1 more strap for 10% off';
    if (n < 5) return `<strong>Free shipping + 10% off unlocked.</strong> Add ${5 - n} more ${plural(5 - n)} for 15% off`;
    return '<strong>Free shipping + 15% off unlocked.</strong>';
  };

  class StrapProgress extends HTMLElement {
    connectedCallback() {
      this.onRefresh = () => this.refresh();
      document.addEventListener('strap-progress:refresh', this.onRefresh);
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribe = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, this.onRefresh);
      }
      // First paint from the server-side count, then confirm with live cart data.
      this.render(parseInt(this.dataset.count || '0', 10));
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('strap-progress:refresh', this.onRefresh);
      if (this.unsubscribe) this.unsubscribe();
    }

    refresh() {
      fetchCart()
        .then((cart) => this.render(countStraps(cart)))
        .catch(() => {});
    }

    render(n) {
      // Three equal segments: 0→2 straps, 2→3, 3→5. Equal widths keep the labels readable on phones.
      let start = 0;
      const steps = MARKS.map((m) => {
        const fill = Math.max(0, Math.min(1, (n - start) / (m.n - start))) * 100;
        start = m.n;
        const isDone = n >= m.n;
        const check = isDone ? '<span class="strap-progress__check" aria-hidden="true">&#10003;</span>' : '';
        return `<li class="strap-progress__step${isDone ? ' is-done' : ''}">
            <span class="strap-progress__bar" aria-hidden="true"><span style="width:${fill}%"></span></span>
            <span class="strap-progress__label">${check}${m.n} straps<br>${m.label}</span>
          </li>`;
      }).join('');

      const icon = n >= 2 ? ICON_GIFT : ICON_TRUCK;
      this.innerHTML = `
        <p class="strap-progress__msg" role="status" aria-live="polite">${icon}<span>${message(n)}</span></p>
        <ol class="strap-progress__steps">${steps}</ol>`;
      this.classList.add('is-ready');
      this.classList.toggle('is-complete', n >= 5);

      // Small "bump" on the message when the count changes after first paint.
      if (this.lastCount !== undefined && this.lastCount !== n) {
        this.classList.remove('is-bump');
        void this.offsetWidth;
        this.classList.add('is-bump');
      }
      this.lastCount = n;
    }
  }

  customElements.define('strap-progress', StrapProgress);

  // Catch cart changes made by apps (e.g. product options) that bypass the theme's events.
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const notify = () => setTimeout(() => document.dispatchEvent(new Event('strap-progress:refresh')), 60);
  const isCartMutation = (url) => {
    try {
      return CART_MUTATION.test(new URL(url, window.location.href).pathname);
    } catch (e) {
      return false;
    }
  };

  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const request = originalFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
    if (url && isCartMutation(url)) request.then(notify, () => {});
    return request;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (url && isCartMutation(String(url))) this.addEventListener('loadend', notify);
    return originalOpen.call(this, method, url, ...rest);
  };

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) notify();
  });
})();
